import { lazy, Suspense, useEffect } from 'react';
import { Link, UNSAFE_LocationContext, useLocation } from 'react-router';
import { PagePanel, usePagePanel } from './page-panel';
import { AnimatePresence, motion, MotionConfig, useReducedMotion } from 'motion/react';
import { preloadPages } from '@/lib/preload';
import { AppHeader } from './app-header';
import { AppContextMenu } from '../context-menus';
import { MobileNavigation } from './mobile-nav';
import { PartyDock } from '../party/party-dock';
import { StreamSelectionModal } from '../streams/stream-modal';
import { DEFAULT_PREFERENCES } from '@teaplus/shared';
import { useActiveProfile } from '@/lib/queries';
import { applyAppearance } from '@/lib/appearance';
import { useUi } from '@/lib/store';

const LookPreview = lazy(() =>
  import('@/routes/settings/appearance').then((m) => ({ default: m.LookPreview })),
);

export function useAppearance() {
  const saved = useActiveProfile().data?.preferences;
  const draft = useUi((s) => s.lookDraft);
  useEffect(() => {
    const root = document.documentElement;
    const prefs = { ...(saved ?? DEFAULT_PREFERENCES), ...draft };
    applyAppearance(prefs);
    root.dataset.density = prefs.density;
    if (prefs.reducedMotion === 'on') root.dataset.motion = 'off';
    else if (prefs.reducedMotion === 'off') root.dataset.motion = 'on';
    else delete root.dataset.motion;
  }, [saved, draft]);
}

const PANEL_LABELS: [RegExp, string][] = [
  [/^\/settings/, 'Settings'],
  [/^\/downloads/, 'Downloads'],
  [/^\/friends/, 'Friends'],
  [/^\/stats/, 'Profile Statistics'],
  [/^\/(movie|series)\//, 'Title details'],
];

export function AppLayout() {
  useAppearance();
  const location = useLocation();
  const { data } = useActiveProfile();
  const previewing = useUi((s) => !!s.lookDraft);
  const draftMotion = useUi((s) => s.lookDraft?.reducedMotion);
  // Reduce motion (Settings → Appearance) wins over the system setting, for animations in code too.
  const motionPref = draftMotion ?? data?.preferences.reducedMotion;
  const systemReduce = useReducedMotion();
  const reduce = motionPref === 'on' || (motionPref !== 'off' && !!systemReduce);
  const panel = usePagePanel();
  // Once the app is open, load the other pages' code in the background so they open at once.
  useEffect(() => {
    const t = window.setTimeout(preloadPages, 400);
    return () => window.clearTimeout(t);
  }, []);
  // The page in front: normally the current one; while a panel is open, the page it opened over.
  const page = panel.open && panel.background ? panel.background : null;
  const pageLocation = page?.location ?? location;
  const pageKey = pageLocation.pathname.startsWith('/settings')
    ? '/settings'
    : pageLocation.pathname;
  return (
    <MotionConfig reducedMotion={reduce ? 'always' : 'never'}>
      <div className="min-h-dvh bg-app pb-[calc(env(safe-area-inset-bottom)+4.5rem)] lg:pb-0">
        <a
          href="#main"
          className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[60] focus:rounded-lg focus:bg-pink focus:px-4 focus:py-2 focus:text-on-pink"
        >
          Skip to content
        </a>
        <AppHeader />
        {/* Held in place (and out of reach) behind an open panel, exactly where it was scrolled. */}
        <div
          inert={!!page}
          aria-hidden={page ? true : undefined}
          className="pr-[var(--tp-dock-w,0px)]"
          style={
            page ? { position: 'fixed', top: -(page.scroll ?? 0), left: 0, right: 0 } : undefined
          }
        >
          <AppContextMenu>
            {/* The new page replaces the old one straight away and fades in (each page shows its
              skeleton while it loads), rather than waiting for the old page to fade out first. */}
            <motion.main
              id="main"
              key={pageKey}
              initial={reduce ? false : { opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
            >
              <Suspense fallback={null}>
                <UNSAFE_LocationContext.Provider
                  value={{ location: pageLocation, navigationType: panel.navigationType }}
                >
                  {page ? page.outlet : panel.outlet}
                </UNSAFE_LocationContext.Provider>
              </Suspense>
            </motion.main>
          </AppContextMenu>
          <Footer kids={data?.profile.isKids} />
        </div>
        <AnimatePresence>
          {panel.open && (
            <PagePanel
              key="panel"
              label={PANEL_LABELS.find(([re]) => re.test(location.pathname))?.[1] ?? 'Panel'}
              onClose={panel.close}
              back={
                panel.backTo
                  ? {
                      label: `Back to ${PANEL_LABELS.find(([re]) => re.test(panel.backTo!))?.[1] ?? 'previous page'}`,
                      onBack: panel.back,
                    }
                  : null
              }
            >
              {panel.outlet}
            </PagePanel>
          )}
        </AnimatePresence>
        <MobileNavigation />
        <PartyDock />
        {previewing && (
          <Suspense fallback={null}>
            <LookPreview />
          </Suspense>
        )}
        <StreamSelectionModal />
      </div>
    </MotionConfig>
  );
}

function Footer({ kids }: { kids?: boolean }) {
  return (
    <footer className="mx-auto mt-16 max-w-[1600px] border-t border-line/60 px-4 py-10 text-[13px] text-muted sm:px-10">
      <p>
        Tea+ shows catalogue information from its configured metadata provider. Availability of
        streams depends entirely on the addons and provider connections you configure
        {kids ? '' : ' and their terms'}; Tea+ does not host media. This product uses the TMDB API
        but is not endorsed or certified by TMDB.
      </p>
      <p className="mt-2 opacity-80">
        © {new Date().getFullYear()} Tea+ ·{' '}
        <Link to="/privacy" className="hover:text-text hover:underline">
          Cookie &amp; Privacy Policy
        </Link>
      </p>
    </footer>
  );
}
